import { useState } from 'react'
import { Loader2 } from 'lucide-react'

interface OAuthProvider {
  name: string
  icon: string
  bgColor: string
  textColor: string
}

const providers: Record<string, OAuthProvider> = {
  google: {
    name: 'Google',
    icon: '🔍',
    bgColor: 'bg-white hover:bg-gray-50',
    textColor: 'text-gray-700',
  },
  github: {
    name: 'GitHub',
    icon: '🐙',
    bgColor: 'bg-gray-900 hover:bg-gray-800',
    textColor: 'text-white',
  },
  microsoft: {
    name: 'Microsoft',
    icon: '🪟',
    bgColor: 'bg-blue-600 hover:bg-blue-700',
    textColor: 'text-white',
  },
}

// Social login is parked behind the backend's SOCIAL_LOGIN_ENABLED flag (#401):
// every /api/oauth/* route answers 501 while it is off. The SPA has no way to
// read that server-side flag at runtime, so it is mirrored here as a build-time
// Vite var (same pattern as VITE_API_URL in lib/api-client.ts) and must be set
// to 'true' explicitly — any other value, including unset, keeps the buttons
// hidden so the default matches the backend default (#404).
const SOCIAL_LOGIN_ENABLED = import.meta.env.VITE_SOCIAL_LOGIN_ENABLED === 'true'

export function OAuthButtons() {
  const [loading, setLoading] = useState<string | null>(null)

  if (!SOCIAL_LOGIN_ENABLED) {
    return null
  }

  const handleOAuthLogin = (provider: string) => {
    setLoading(provider)
    // Redirect to OAuth authorization endpoint
    window.location.href = `/api/oauth/authorize/${provider}?return_url=${encodeURIComponent(window.location.origin + '/dashboard')}`
  }

  return (
    <div className="space-y-3">
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="bg-white px-2 text-gray-500">Or continue with</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3">
        {Object.entries(providers).map(([key, provider]) => (
          <button
            key={key}
            onClick={() => handleOAuthLogin(key)}
            disabled={loading !== null}
            className={`flex w-full items-center justify-center rounded-md border border-gray-300 px-4 py-2 text-sm font-medium shadow-sm transition-colors ${
              provider.bgColor
            } ${provider.textColor} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {loading === key ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <span className="mr-2 text-lg">{provider.icon}</span>
            )}
            Continue with {provider.name}
          </button>
        ))}
      </div>
    </div>
  )
}